import React from "react";
import { IWDDesignModeProps } from "../common";
import { IContextProvider } from "../../../uxp";
import { IDataSource, IDataSourceBinding, IQueryDataSource, processSource, TargetDataStructure } from "./datasource";
import { IContextData } from "../../../hooks/useContextData";
import { LoadingSpinner, useAlert, useToast } from "uxp/components";
import { getQueryBuilder } from "./source-picker-helper";
import { useQEServices } from "./lucy-query-builder/useQEServices";
import { DataSourceBrowser } from "./datasource-editor/model-action-browser";
import { generateUUID, tryParseJSON } from "../../../util";

// source picker
interface SourcePickerProps {
    designer: IWDDesignModeProps,
    uxpContext: IContextProvider,
    selectedSource: IDataSource,
    existingSources: IDataSource[],
    onSelect: (source: IDataSource) => void,
    onRemove: (id: string) => void,
    contextData: IContextData,
    onClose: () => void,
    target: TargetDataStructure
    show: boolean
}

export const SourcePickerWrapper: React.FC<SourcePickerProps> = (props) => {

    const { designer, uxpContext, selectedSource, existingSources, onSelect, onRemove, contextData, onClose, target, show } = props

    const [hasQB, setHasQB] = React.useState(false)
    const [loading, setLoading] = React.useState(true)

    const alerts = useAlert()
    const toast = useToast()
    const qeServices = useQEServices(uxpContext)

    React.useEffect(() => {
        getQueryBuilderIfEnabled()
    }, [])

    async function getQueryBuilderIfEnabled() {
        setLoading(true)
        const qb = await getQueryBuilder(uxpContext)
        setHasQB(!!qb)
        setLoading(false)
    }

    async function handleExternalSourcSelect(source: any) {
        try {
            let res = await processSource(designer, uxpContext, {}, source, target, existingSources, selectedSource?.id);
            if (res?.source) {
                const _sources = res?.datasources || []
                const _source = _sources.find((s: any) => s.id == (res.source as IDataSourceBinding).source)
                onSelect(_source);
                return true;
            }
            else {
                alerts.show(uxpContext.$L('widgetdesigner.messages.source-parse-failed'))
                return false
            }
        }
        catch (e) {
            alerts.show({ title: uxpContext.$L('widgetdesigner.messages.invalid-source-title'), content: uxpContext.$L('widgetdesigner.messages.invalid-source-body') });
            return false;
        }
    }

    // Same processing as handleExternalSourcSelect but WITHOUT persisting (no
    // onSelect). Drives the preview table; confirm still re-runs the funnel above.
    async function processSourceOnly(source: any): Promise<IDataSource | null> {
        try {
            const res = await processSource(designer, uxpContext, {}, source, target, existingSources, selectedSource?.id);
            if (res?.source) {
                const _sources = res?.datasources || []
                return _sources.find((s: any) => s.id == (res.source as IDataSourceBinding).source) || null
            }
            return null
        }
        catch (e) {
            return null
        }
    }

    function renderExternalSourceBrowser() {
        return designer?.sourceBrowser(uxpContext, null, onClose, handleExternalSourcSelect, selectedSource?.__source__ || null, contextData)
    }

    async function saveQueryChanges(uxpContext: IContextProvider, source: IQueryDataSource): Promise<{ status: 'done' | 'error', message: string, queryId?: string }> {
        try {
            const service = `Query:Create`
            const params: any = {
                Name: source.name,
                Description: source?.description || '',
                ID: generateUUID(),
                Configuration: JSON.stringify(source)
            }

            const res = await uxpContext.executeService('Lucy', service, params, { json: true })
            if (!!res?.[0]?.['Key']) {
                return { status: 'done', message: uxpContext.$L('widgetdesigner.messages.query-saved'), queryId: params.ID }
            }
            console.log('Invalid response for saving query', res)
            return { status: 'error', message: uxpContext.$L('widgetdesigner.messages.invalid-response') }
        }
        catch (e) {
            console.log('Error saving query', e)
            return { status: 'error', message: e }
        }
    }


    // Edit an EXISTING saved query record (Query:Update) — never touches the
    // widget. Merges the edited {name, description, query, schema} into the
    // record's existing Configuration (preserving whatever writer shape/extra
    // fields it already has) and persists. Returns the refreshed record so the
    // preview page can re-render.
    async function updateSavedQuery(
        record: any,
        changes: { name: string, description: string, query: any, schema: any }
    ): Promise<{ status: 'done' | 'error', message?: string, record?: any }> {
        try {
            const existing = tryParseJSON(record?.Configuration) || {}
            const mergedConfig = {
                ...existing,
                name: changes.name,
                description: changes.description,
                query: changes.query,
                schema: changes.schema
            }
            const configuration = JSON.stringify(mergedConfig)

            await uxpContext.executeService('Lucy', 'Query:Update', {
                Key: record.Key,
                ID: record.ID,
                Name: changes.name,
                Description: changes.description || '',
                Configuration: configuration
            }, { json: true })

            // Refresh the engine catalog so the preview re-fetch returns fresh data.
            await qeServices?.refreshDatasources?.()

            toast.success(uxpContext.$L('widgetdesigner.messages.query-updated'))
            const updatedRecord = {
                ...record,
                Name: changes.name,
                Description: changes.description || '',
                Configuration: configuration
            }
            return { status: 'done', record: updatedRecord }
        }
        catch (e) {
            console.log('Error updating query', e)
            alerts.show(uxpContext.$L('widgetdesigner.messages.query-update-failed'))
            return { status: 'error', message: String(e) }
        }
    }

    if (!show) return null
    if (loading) {
        return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', minHeight: '10rem' }}>
            <LoadingSpinner />
        </div>
    }
    if (designer?.sourceBrowser) {
        return renderExternalSourceBrowser()
    }

    return <DataSourceBrowser
        selectedSource={selectedSource}
        existingSources={existingSources}
        onSelected={onSelect}
        onRemove={onRemove}
        context={uxpContext}
        contextData={contextData}
        onClose={onClose}
        enableQuery={hasQB}
        onSelectQuery={handleExternalSourcSelect}
        onProcessSource={processSourceOnly}
        onSaveQuery={(s) => saveQueryChanges(uxpContext, s as IQueryDataSource)}
        onUpdateQuery={updateSavedQuery}
    />
}
